import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { useSeo, useHreflang } from "@/components/seo/Seo";
import { LANGS, STATIC_COPY, S, p } from "@/lib/seoContent";
import { lp } from "@/lib/seoContent";

export default function Faq() {
  const { t, lang } = useLang();
  const seoPage = STATIC_COPY.faq[lang] || STATIC_COPY.faq.fr;
  useSeo({ lang, title: seoPage.title, description: seoPage.meta, path: p(lang, "faq"), type: "faq", id: "" });
  useHreflang(Object.fromEntries(LANGS.map((l) => [l, p(l, "faq")])));
  const [open, setOpen] = useState(0);

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="faq-page">
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-14 pt-32 lg:pt-40">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("faq.eyebrow")}</p>
          <h1 className="mt-5 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            {t("faq.h1a")} <span className="font-light text-slate/50">{t("faq.h1b")}</span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate">
            {t("faq.sub")}
          </p>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-4xl space-y-3 px-5 sm:px-8">
          {t("faq.items").map(([q, a], i) => (
            <motion.div key={q} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.03 }}
              className="overflow-hidden rounded-2xl border border-ink/10 bg-white transition-colors hover:border-miles/25"
              data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-question-${i}`}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
                <span className="text-sm font-semibold text-ink sm:text-base">{q}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-miles transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} strokeWidth={1.5} />
              </button>
              <div className={`grid transition-[grid-template-rows] duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <p className="px-6 pb-6 text-sm leading-relaxed text-slate" data-testid={`faq-answer-${i}`}>{a}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-4xl px-5 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-miles-deep px-8 py-8">
            <p className="text-lg font-bold text-white">{t("faq.band")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={lp(lang, "/contact")} data-testid="faq-contact-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-miles-deep transition-all hover:-translate-y-0.5">
                {t("faq.band_contact")}
              </Link>
              <Link to="/reservation" data-testid="faq-book-cta"
                className="inline-flex items-center gap-2 rounded-full border border-miles-sky/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-miles-sky">
                {t("faq.band_book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
